import { createContext, useReducer, ReactNode, useEffect } from "react";
import accountReducer from "./accountReducer";
import axios from "axios";
import { EnvironmentVariables } from "@/modules/common/types";
import { jwtDecode } from "jwt-decode";
import {
  CognitoUser,
  CognitoRefreshToken,
  CognitoAccessToken,
  CognitoUserSession,
  CognitoIdToken,
  CognitoUserPool,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { PathConstants } from "@/routes/constants";
import Cookies from "js-cookie";

interface CognitoTokens {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
}

const initialState = {
  isLoggedIn: false,
  isBetaUser: false,
  isAlphaUser: false,
  isInitialized: false,
  user: null,
};

export interface DecodedToken {
  sub: string;
  "cognito:username": string;
  email: string;
  email_verified: boolean;
  name: string;
  phone_number: string;
  "cognito:groups": string[];
  isSupervisor: string;
  crmId: string;
  exp: number;
}

interface AuthContextProps {
  isLoggedIn: boolean;
  isBetaUser: boolean;
  isAlphaUser: boolean;
  isInitialized: boolean;
  user: DecodedToken | null;
  logout: () => Promise<void>;
  refreshUserState: () => Promise<void>;
  setUserState: (token: string) => Promise<void>;
  getDecodedToken: () => DecodedToken | null;
  authorize: (authorizationCode: string) => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  resendVerificationCode: (username: string) => Promise<void>;
  confirmPasswordReset: (
    username: string,
    verification_code: string,
    new_password: string
  ) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  getTokenByKey: (tokenKey: string) => string | null;
  login: (email: string, password: string) => Promise<void>;
  completePasswordChallenge: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | null>(null);

interface AWSCognitoProviderProps {
  children: ReactNode;
}

// Helper functions
const getTokenExpiration = (token: string): number => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch {
    return 0;
  }
};

const shouldRefreshToken = (expirationTime: number): boolean => {
  const currentTime = Date.now();
  const timeUntilExpiry = expirationTime - currentTime;
  // Refresh if token will expire in less than 5 minutes
  return timeUntilExpiry < 5 * 60 * 1000;
};

const retryWithBackoff = async (fn: () => Promise<any>, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, i) * 1000)
      );
    }
  }
};

export const AWSCognitoProvider = ({ children }: AWSCognitoProviderProps) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const userPool = new CognitoUserPool({
    UserPoolId: import.meta.env.REACT_APP_AWS_POOL_ID,
    ClientId: import.meta.env.REACT_APP_AWS_CLIENT_ID,
  });

  const setToken = (tokenName: string, tokenValue: string) => {
    localStorage.setItem(tokenName, tokenValue);
    Cookies.set(tokenName, tokenValue, {
      domain: EnvironmentVariables.COOKIES_SHARED_DOMAIN.toString(),
      path: "/",
      secure: true,
      sameSite: "None",
      expires: 1, // 1 day; adjust as needed
    });
  };

  const getTokenByKey = (tokenKey: string): string | null => {
    const usernameString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.LastAuthUser`;
    const username = localStorage.getItem(usernameString);
    if (username) {
      const tokenString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.${tokenKey}`;
      const localStorageToken = localStorage.getItem(tokenString);
      if (localStorageToken) return localStorageToken;
    }
    // Fallback to cookies before giving up
    return Cookies.get(tokenKey) ?? null;
  };

  const getDecodedToken = (): DecodedToken | null => {
    const token = getTokenByKey("idToken");
    if (!token) return null;
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      // Expired token must not count as a logged-in user (it would trigger
      // authorized requests on public pages and cause a 401/logout reload loop)
      if (decoded.exp && decoded.exp * 1000 < Date.now()) return null;
      return decoded;
    } catch (error) {
      return null;
    }
  };

  // Token refresh management
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    const setupTokenRefresh = () => {
      const checkAndRefreshToken = async () => {
        // Read token fresh each tick — a captured snapshot would keep
        // evaluating the pre-refresh token and re-trigger refresh every minute
        const idToken = getTokenByKey("idToken");
        if (!idToken) return;
        const expiration = getTokenExpiration(idToken);
        if (shouldRefreshToken(expiration)) {
          try {
            await retryWithBackoff(() => refreshUserState());
          } catch (error) {
            console.error("Token refresh failed:", error);
          }
        }
      };

      // Check token every minute
      refreshInterval = setInterval(checkAndRefreshToken, 60 * 1000);
      // Also check immediately
      checkAndRefreshToken();
    };

    if (state.isLoggedIn) {
      setupTokenRefresh();
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [state.isLoggedIn]);

  useEffect(() => {
    const syncTokensToCookies = () => {
      const usernameString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.LastAuthUser`;
      const username = localStorage.getItem(usernameString);
      if (!username) return;

      const tokenKeys = ["idToken", "accessToken", "refreshToken", "expTime"];
      tokenKeys.forEach((key) => {
        const localStorageKey =
          key === "expTime"
            ? "expTime"
            : `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.${key}`;
        const token = localStorage.getItem(localStorageKey);
        if (token) {
          Cookies.set(key, token, {
            domain: EnvironmentVariables.COOKIES_SHARED_DOMAIN.toString(),
            path: "/",
            secure: true,
            sameSite: "None",
            expires: 1,
          });
        }
      });
    };

    syncTokensToCookies();
  }, []);

  const setUserState = async (token: string) => {
    const decodedToken: DecodedToken = jwtDecode(token);

    localStorage.setItem(
      "userRoles",
      (decodedToken["cognito:groups"] ?? []).join(",")
    );

    setToken("expTime", decodedToken.exp.toString());

    // Ensure idToken is stored in cookies if not already stored
    const currentIdToken = getTokenByKey("idToken");
    if (currentIdToken && currentIdToken !== token) {
      setToken("idToken", token);
    }

    dispatch({
      type: "LOGIN",
      payload: {
        isLoggedIn: true,
        user: {
          cognitoId: decodedToken.sub,
          username: decodedToken["cognito:username"],
          email: decodedToken.email,
          name: decodedToken.name,
          phone: decodedToken.phone_number,
          isSupervisor:
            decodedToken.isSupervisor === "false" ? "false" : "true",
          crmId: decodedToken.crmId,
        },
      },
    });
  };

  const refreshUserState = async () => {
    const refreshToken = getTokenByKey("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const body = `grant_type=refresh_token&client_id=${EnvironmentVariables.AWS_CLIENT_ID}&refresh_token=${refreshToken}&redirect_uri=${EnvironmentVariables.COGNITO_REDIRECT_URI}`;

    try {
      const axiosService = axios.create();
      const response = await axiosService.post(
        `${EnvironmentVariables.COGNITO_DOMAIN}/oauth2/token`,
        body,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      localStorage.setItem(
        "expTime",
        (response.data.expires_in + Date.now()).toString()
      );
      setToken("expTime", (response.data.expires_in + Date.now()).toString());

      const usernameString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.LastAuthUser`;
      const username = localStorage.getItem(usernameString);

      if (!username) {
        throw new Error("Username not found in storage");
      }

      // Update all tokens
      if (response.data.id_token) {
        setToken(
          `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.idToken`,
          response.data.id_token
        );
        setToken("idToken", response.data.id_token);
      }

      if (response.data.access_token) {
        setToken(
          `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.accessToken`,
          response.data.access_token
        );
        setToken("accessToken", response.data.access_token);
      }

      await setUserState(response.data.id_token);
      return response.data;
    } catch (error: any) {
      console.error("Token refresh failed:", error);
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        throw error;
      }
      throw new Error("Failed to refresh tokens");
    }
  };

  const createUserSession = (tokens: CognitoTokens) => {
    const accessToken = new CognitoAccessToken({
      AccessToken: tokens.access_token,
    });
    const refreshToken = new CognitoRefreshToken({
      RefreshToken: tokens.refresh_token,
    });
    const idToken = new CognitoIdToken({
      IdToken: tokens.id_token,
    });
    const sessionData = {
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
    };
    const session = new CognitoUserSession(sessionData);

    const userData = {
      Username: accessToken.payload.username,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);
    cognitoUser.setSignInUserSession(session);

    // Store tokens in localStorage and cookies
    setToken("idToken", tokens.id_token);
    setToken("accessToken", tokens.access_token);
    setToken("refreshToken", tokens.refresh_token);

    setUserState(tokens.id_token);
  };

  const authorize = async (authorizationCode: string) => {
    const body = `grant_type=authorization_code&client_id=${EnvironmentVariables.AWS_CLIENT_ID}&code=${authorizationCode}&redirect_uri=${EnvironmentVariables.COGNITO_REDIRECT_URI}`;
    const response = await axios.post(
      `${EnvironmentVariables.COGNITO_DOMAIN}/oauth2/token`,
      body,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
    createUserSession(response.data);
  };

  const sendVerificationCode = async (email: string): Promise<void> => {
    const usr = new CognitoUser({
      Username: email,
      Pool: userPool,
    });
    return new Promise((resolve, reject) => {
      usr.forgotPassword({
        onSuccess: () => {
          dispatch({
            type: "VERIFICATION_CODE",
            payload: {
              isLoggedIn: false,
              user: {
                email,
              },
            },
          });
          resolve();
        },
        onFailure: (_err) => {
          reject(_err);
        },
      });
    });
  };

  const resendVerificationCode = async (username: string): Promise<void> => {
    const usr = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    return new Promise((resolve, reject) => {
      usr.resendConfirmationCode((err, result) => {
        if (result) {
          resolve();
        }
        if (err) {
          reject(err);
        }
      });
    });
  };

  const confirmPasswordReset = async (
    username: string,
    verification_code: string,
    new_password: string
  ): Promise<void> => {
    const usr = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    return new Promise((resolve, reject) => {
      usr.confirmPassword(verification_code, new_password, {
        onSuccess: () => {
          dispatch({
            type: "PASSWORD_CONFIRMED",
            payload: {
              isLoggedIn: false,
              user: null,
            },
          });
          resolve();
        },
        onFailure: (_err) => {
          reject(_err);
        },
      });
    });
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const user = userPool.getCurrentUser();
      if (user) {
        user.getSession((_: any, res: any) => {
          if (res) {
            user.changePassword(oldPassword, newPassword, (error, result) => {
              if (error) {
                reject(error);
              }
              if (result) {
                resolve();
              }
            });
          }
        });
      }
    });
  };

  const login = async (email: string, password: string): Promise<void> => {
    const usr = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authData = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise<void>((resolve, reject) => {
      usr.authenticateUser(authData, {
        onSuccess: (session) => {
          // Store tokens in cookies after successful authentication
          setToken("idToken", session.getIdToken().getJwtToken());
          setToken("accessToken", session.getAccessToken().getJwtToken());
          setToken("refreshToken", session.getRefreshToken().getToken());

          setUserState(session.getIdToken().getJwtToken());
          resolve();
        },
        newPasswordRequired: () => {
          dispatch({
            type: "PASSWORD_CHALLENGE",
            payload: {
              isLoggedIn: false,
              user: {
                email: email,
                password: password,
              },
            },
          });
          resolve();
        },
        onFailure: (_err) => {
          reject(_err);
        },
      });
    });
  };

  const completePasswordChallenge = async (
    newPassword: string
  ): Promise<void> => {
    const usr = new CognitoUser({
      Username: state?.user?.email,
      Pool: userPool,
    });

    const authData = new AuthenticationDetails({
      Username: state?.user?.email,
      Password: state?.user?.password,
    });

    return new Promise<void>((resolve, reject) => {
      usr.authenticateUser(authData, {
        onSuccess: (session) => {
          // Store tokens in cookies after successful authentication
          setToken("idToken", session.getIdToken().getJwtToken());
          setToken("accessToken", session.getAccessToken().getJwtToken());
          setToken("refreshToken", session.getRefreshToken().getToken());

          setUserState(session.getIdToken().getJwtToken());
          resolve();
        },
        newPasswordRequired: () => {
          usr.completeNewPasswordChallenge(
            newPassword,
            {},
            {
              onSuccess: (session) => {
                // Store tokens in cookies after successful password challenge completion
                setToken("idToken", session.getIdToken().getJwtToken());
                setToken("accessToken", session.getAccessToken().getJwtToken());
                setToken("refreshToken", session.getRefreshToken().getToken());

                setUserState(session.getIdToken().getJwtToken());
                resolve();
              },
              onFailure: (_err) => {
                reject(_err);
              },
            }
          );
        },
        onFailure: (_err) => {
          reject(_err);
        },
      });
    });
  };

  const logout = async () => {
    try {
      const isFranchise = EnvironmentVariables.IS_FRANCHISE.toString();
      const usernameString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.LastAuthUser`;
      const username = localStorage.getItem(usernameString);

      // Clear all auth related items from localStorage
      if (username) {
        const tokenKeys = [
          "idToken",
          "refreshToken",
          "accessToken",
          "clockDrift",
        ];
        tokenKeys.forEach((tokenKey) => {
          const tokenString = `CognitoIdentityServiceProvider.${EnvironmentVariables.AWS_CLIENT_ID}.${username}.${tokenKey}`;
          localStorage.removeItem(tokenString);
        });
      }

      localStorage.removeItem(usernameString);
      localStorage.removeItem("expTime");
      localStorage.removeItem("userRoles");

      // Clear all cookies — remove with and without the shared-domain attribute,
      // otherwise a cookie set by another host on the shared domain (or set
      // before the domain env changed) survives logout and getTokenByKey keeps
      // resolving a stale token, causing an endless 401 → logout → reload loop
      ["expTime", "idToken", "accessToken", "refreshToken"].forEach((key) => {
        Cookies.remove(key, {
          domain: EnvironmentVariables.COOKIES_SHARED_DOMAIN.toString(),
          path: "/",
        });
        Cookies.remove(key, { path: "/" });
        Cookies.remove(key);
      });

      // Update state
      dispatch({
        type: "LOGOUT",
        payload: {
          isLoggedIn: false,
          user: null,
        },
      });

      // Handle redirect — skip when already on the login page to avoid
      // reloading it in a loop
      const loginPath =
        isFranchise === "true"
          ? PathConstants.FRANCHISE_LOGIN
          : PathConstants.LOGIN;
      if (window.location.pathname !== loginPath) {
        window.location.href = `${EnvironmentVariables.CRM_NAVIGATE_URL}${loginPath}`;
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Force redirect to login even if cleanup fails — but never reload the
      // page we're already on (login or franchise-login) to avoid a loop
      const fallbackLoginPath =
        EnvironmentVariables.IS_FRANCHISE.toString() === "true"
          ? PathConstants.FRANCHISE_LOGIN
          : PathConstants.LOGIN;
      if (window.location.pathname !== fallbackLoginPath) {
        window.location.href = `${EnvironmentVariables.CRM_NAVIGATE_URL}${fallbackLoginPath}`;
      }
    }
  };

  // Route protection effect
  useEffect(() => {
    const pathname = window.location.pathname;
    const publicRoutes = [
      PathConstants.LOGIN,
      PathConstants.FRANCHISE_LOGIN,
      PathConstants.SEND_EMAIL_FRANCHISE,
      PathConstants.CHANGE_PASSWORD_FRANCHISE,
      PathConstants.AUTH_CALLBACK,
      PathConstants.AUTH_CHALLENGE,
    ];

    const isPublicRoute = publicRoutes.includes(pathname);
    const isFranchise = EnvironmentVariables.IS_FRANCHISE.toString() === "true";

    // Handle franchise vs regular login redirects
    if (isFranchise && pathname === "/login") {
      window.location.href = `${EnvironmentVariables.CRM_NAVIGATE_URL}${PathConstants.FRANCHISE_LOGIN}`;
      return;
    }

    if (!isFranchise && pathname === "/franchise-login") {
      window.location.href = `${EnvironmentVariables.CRM_NAVIGATE_URL}${PathConstants.LOGIN}`;
      return;
    }

    if (state.isLoggedIn && isPublicRoute && state.isInitialized) {
      window.location.href = `${EnvironmentVariables.CRM_NAVIGATE_URL}/`;
    }
  }, [state.isLoggedIn, state.isInitialized]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout,
        refreshUserState,
        setUserState,
        getDecodedToken,
        authorize,
        user: getDecodedToken(),
        sendVerificationCode,
        resendVerificationCode,
        confirmPasswordReset,
        changePassword,
        getTokenByKey,
        login,
        completePasswordChallenge,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
